// index.jsx
import React, { useEffect, useState } from "react";
import {
  DndContext,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";

import { useStyles } from "./style";
import { Column } from "./containers";
import { useSelector } from "react-redux";
import {
  updateSkypointProjectTask,
  deleteSkypointProjectTaskStatus,
  getSkypointBoardColumn,
  loadSkypointBoard,
  refreshSkypointBoard,
  clearSkypointBoard,
} from "../../services/skypointProject";
import TaskCard from "./TaskCard";
import { ActionIcon, Box } from "@mantine/core";
import { LuPlus } from "react-icons/lu";
import { useDisclosure } from "@mantine/hooks";
import TaskAddStatusModalSkypoint from "./TaskAddStatusModalSkypoint";
import { isStaff } from "../../http";

/* ------------------ MAIN BOARD ------------------ */
export default function KanbanBoard() {
  const { classes } = useStyles();
  const { profile } = useSelector((state) => state.profile);
  const { skypointBoard, skypointProjectStatus } = useSelector(
    (state) => state?.skypointProject
  );

  // Every loaded task, column by column; drag and drop moves them here before the server answers
  const [tasks, setTasks] = useState([]);
  const [
    taskAddStatusModalOpened,
    { open: taskAddStatusModalOpen, close: taskAddStatusModalClose },
  ] = useDisclosure(false);

  // Each column loads its own pages: page "all" stops at 200 tasks (OD-1694)
  useEffect(() => {
    loadSkypointBoard();
  }, [skypointProjectStatus]);

  useEffect(() => clearSkypointBoard, []);

  useEffect(() => {
    setTasks(
      (skypointProjectStatus || []).flatMap(
        (s) => skypointBoard?.[s?.id]?.tasks || []
      )
    );
  }, [skypointBoard, skypointProjectStatus]);

  const [activeTask, setActiveTask] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = (event) => {
    setActiveTask(tasks.find((t) => t.id === event.active.id));
  };

  const handleDragOver = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setTasks((prev) => {
      const oldIndex = prev.findIndex((t) => t.id === active.id);
      const newIndex = prev.findIndex((t) => t.id === over.id);

      if (oldIndex === -1 || newIndex === -1) return prev;

      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleDragEnd = async ({ active, over }) => {
    setActiveTask(null);
    if (!over) return;

    const activeTask = tasks.find((t) => t.id === active.id);
    if (!activeTask) return;

    let newStatusId;

    // ✅ Case 1: drop directly on column
    const statusFromColumn = skypointProjectStatus.find(
      (s) => s.id === over.id
    );

    if (statusFromColumn) {
      newStatusId = statusFromColumn.id;
    } else {
      // ✅ Case 2: drop on another task → use that task's status
      const overTask = tasks.find((t) => t.id === over.id);
      newStatusId = overTask?.status?.id;
    }

    if (!newStatusId || activeTask.status.id === newStatusId) return;

    const newStatusObj = skypointProjectStatus.find(
      (s) => s.id === newStatusId
    );

    // 🔄 UI update
    setTasks((prev) =>
      prev.map((t) => (t.id === active.id ? { ...t, status: newStatusObj } : t))
    );

    // 🌐 API update
    try {
      await updateSkypointProjectTask(
        { id: active.id, status: newStatusId },
        refreshSkypointBoard
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className={classes.boardContainer}>
        {skypointProjectStatus?.map((statusObj) => (
          <Column
            key={statusObj?.id}
            id={statusObj?.id}
            title={statusObj?.status}
            tasks={tasks?.filter((t) => t?.status?.id === statusObj?.id)}
            loaded={skypointBoard?.[statusObj?.id]?.tasks?.length || 0}
            count={skypointBoard?.[statusObj?.id]?.count || 0}
            loading={skypointBoard?.[statusObj?.id]?.loading}
            onLoadMore={() =>
              getSkypointBoardColumn(
                statusObj?.id,
                (skypointBoard?.[statusObj?.id]?.page || 0) + 1
              )
            }
            general={statusObj?.general}
            onDeleteStatus={
              !isStaff(profile?.user?.role)
                ? (id) => deleteSkypointProjectTaskStatus(id)
                : undefined
            }
          />
        ))}
        {!isStaff(profile?.user?.role) && (
          <Box className={classes.addBtnAbsoluteBox}>
            <ActionIcon onClick={taskAddStatusModalOpen}>
              <LuPlus color="#262626" size={28} />
            </ActionIcon>
          </Box>
        )}
        <TaskAddStatusModalSkypoint
          opened={taskAddStatusModalOpened}
          onClose={taskAddStatusModalClose}
        />
      </div>

      <DragOverlay>{activeTask && <TaskCard task={activeTask} />}</DragOverlay>
    </DndContext>
  );
}
